import type { ProjectDetailsStoryboardShot } from "@/types/project";
import { parseUploadedImages } from "@/services/project-entity";

export function buildVideoShotSaveIfChanged(args: {
  readonly storyboardShots: readonly ProjectDetailsStoryboardShot[];
  readonly shotId: string;
  readonly descriptionText: string;
  readonly referenceImageIds: readonly string[];
  readonly effectiveVideoModel: string;
  readonly lastUsedVideoModel: string | undefined;
  readonly assumedVideoDuration: number;
}): { readonly updatedShots: ProjectDetailsStoryboardShot[]; readonly modelChanged: boolean } | null {
  const shotIndex = args.storyboardShots.findIndex((shot) => shot.id === args.shotId);
  if (shotIndex === -1) {
    return null;
  }

  const shotRow = args.storyboardShots[shotIndex];
  const serverDescription = shotRow?.["video-gen-prompt"] ?? "";
  const sanitizedImageIds = parseUploadedImages(args.referenceImageIds);
  const serverImageIds = shotRow?.["video-uploaded-images"] ?? [];
  const serverDuration = shotRow?.["assumed-video-duration"];
  const normalizedDuration =
    Number.isFinite(args.assumedVideoDuration) && args.assumedVideoDuration > 0
      ? Math.round(args.assumedVideoDuration)
      : 8;
  const imagesChanged =
    sanitizedImageIds.length !== serverImageIds.length ||
    sanitizedImageIds.some((imageId, index) => imageId !== serverImageIds[index]);
  const descriptionChanged = args.descriptionText !== serverDescription.trim();
  const modelChanged = args.lastUsedVideoModel !== args.effectiveVideoModel;
  const durationChanged = serverDuration !== normalizedDuration;

  if (!descriptionChanged && !imagesChanged && !modelChanged && !durationChanged) {
    return null;
  }

  const updatedShots = args.storyboardShots.map((shot, index) =>
    (index === shotIndex
      ? {
          ...shot,
          "video-gen-prompt": args.descriptionText,
          "video-uploaded-images": [...sanitizedImageIds],
          "assumed-video-duration": normalizedDuration
        }
      : shot)
  );

  return { updatedShots, modelChanged };
}

export function buildFrameShotSaveIfChanged(args: {
  readonly storyboardShots: readonly ProjectDetailsStoryboardShot[];
  readonly shotId: string;
  readonly descriptionText: string;
  readonly selectedCharacterImageIds: readonly string[];
  readonly selectedLocationImageIds: readonly string[];
  readonly effectiveImageModel: string;
  readonly lastUsedImageModel: string | undefined;
}): { readonly updatedShots: ProjectDetailsStoryboardShot[]; readonly modelChanged: boolean } | null {
  const shotIndex = args.storyboardShots.findIndex((shot) => shot.id === args.shotId);
  if (shotIndex === -1) {
    return null;
  }

  const shotRow = args.storyboardShots[shotIndex];
  const serverDescription = shotRow?.["ref-image-prompt"] ?? "";
  const serverCharacterIds = shotRow?.["selected-character-images"] ?? [];
  const serverLocationIds = shotRow?.["selected-location-images"] ?? [];

  const descriptionChanged = args.descriptionText !== serverDescription;
  const charactersChanged =
    args.selectedCharacterImageIds.length !== serverCharacterIds.length ||
    args.selectedCharacterImageIds.some((id, index) => id !== serverCharacterIds[index]);
  const locationsChanged =
    args.selectedLocationImageIds.length !== serverLocationIds.length ||
    args.selectedLocationImageIds.some((id, index) => id !== serverLocationIds[index]);
  const modelChanged = args.lastUsedImageModel !== args.effectiveImageModel;

  if (!descriptionChanged && !charactersChanged && !locationsChanged && !modelChanged) {
    return null;
  }

  const updatedShots = args.storyboardShots.map((shot, index) =>
    (index === shotIndex
      ? {
          ...shot,
          "ref-image-prompt": args.descriptionText,
          "selected-character-images": [...args.selectedCharacterImageIds],
          "selected-location-images": [...args.selectedLocationImageIds]
        }
      : shot)
  );

  return { updatedShots, modelChanged };
}
